import {SyncStatusItem} from '../interfaces/core/sync-status-item';
import {Table} from 'dexie';
import {DateTime} from 'luxon';
import {SyncItem} from '../interfaces/core/sync-item';
import {db} from './db.service';
import {TableName} from '../enums/core/table-name';
import {SyncException} from '../exceptions/sync.exception';
import {Subject} from 'rxjs';
import {Injectable} from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class SyncStatusItemsTable {

  private readonly _table: Table<SyncStatusItem, number>;
  private readonly _change$: Subject<void>;

  constructor() {
    this._table = db.syncStatusItems;
    this._change$ = new Subject<void>();
  }

  get change$(): Subject<void> {
    return this._change$;
  }

  /**
   *
   */
  public async getToSync(): Promise<SyncStatusItem[]> {
    return this._table.where('isSynced').equals(0).sortBy('id');
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Single item
  // -------------------------------------------------------------------------------------------------------------------
  public async getByPrimaryKey(primaryKey: string): Promise<SyncStatusItem> {
    return this._table.where('primaryKey').equals(primaryKey).first();
  }

  /**
   *
   * @param tableName
   * @param primaryKey
   * @param isSynced
   */
  public async create(tableName: TableName, primaryKey: string, isSynced: boolean = false): Promise<void> {
    await this._table.add(this.setBasicProperties(tableName, primaryKey, isSynced));
    this._change$.next();
  }

  /**
   *
   * @param primaryKey
   * @param isSynced
   */
  public async update(primaryKey: string, isSynced: boolean = false): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      let syncStatusItem = await this._table.where('primaryKey').equals(primaryKey).first();
      // Ohne Eintrag gibt es nichts zu aktualisieren; ein TypeError würde die umgebende Transaktion abbrechen
      if(!syncStatusItem) {
        return;
      }
      syncStatusItem = this.setUpdateProperties(syncStatusItem, isSynced);
      await this._table.update(syncStatusItem.id, syncStatusItem);
    });
    this._change$.next();
  }

  /**
   *
   * @param tableName
   * @param primaryKey
   * @param isSynced
   */
  public async addOrUpdate(tableName: TableName, primaryKey: string, isSynced: boolean = false): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      let syncStatusItem = await this._table.where('primaryKey').equals(primaryKey).first();
      if(syncStatusItem) {
        await this.update(primaryKey, isSynced);
      }
      else {
        await this.create(tableName, primaryKey, isSynced);
      }
    });
  }

  /**
   *
   * @param primaryKey
   * @param force
   */
  public async remove(primaryKey: string, force: boolean = false): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      const syncStatusItem = await this._table.where('primaryKey').equals(primaryKey).first();
      // Ohne Eintrag existiert kein Sync-Vertrag; ein TypeError würde die umgebende Transaktion
      // samt lokaler Löschung zurückrollen
      if(!syncStatusItem) {
        return;
      }
      if(this.canBeRemovedWithoutSync(syncStatusItem, force)) {
        await this._table.delete(syncStatusItem.id);
      }
      else {
        await this.setToDeleted(syncStatusItem.id);
      }
    });
    this._change$.next();
  }

  /**
   * A queue entry may only be removed completely if it was never synchronised and no upload has claimed it.
   * A running upload can still create the record on the server, so a tombstone is required in that case.
   *
   * @param syncStatusItem
   * @param force
   * @private
   */
  private canBeRemovedWithoutSync(syncStatusItem: SyncStatusItem, force: boolean): boolean {
    return force || (!!!syncStatusItem.lastSync && syncStatusItem.isSyncing === 0);
  }

  /**
   *
   * @param id
   */
  private async setToDeleted(id: number): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      let syncStatusItem = await this._table.where('id').equals(id).first();
      syncStatusItem = this.setUpdateProperties(syncStatusItem);
      syncStatusItem.deleted = 1;
      await this._table.update(syncStatusItem.id, syncStatusItem);
    });
  }

  /**
   *
   * @param tableName
   */
  public async removeByTableName(tableName: string): Promise<void> {
    await this._table.where('tableName').equals(tableName).delete();
    this._change$.next();
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Many items
  // -------------------------------------------------------------------------------------------------------------------

  /**
   *
   * @param primaryKeys
   */
  public async getAllByPrimaryKey(primaryKeys: string[]): Promise<SyncStatusItem[]> {
    return this._table.where('primaryKey').anyOf(primaryKeys).toArray();
  }

  /**
   *
   * @param tableName
   * @param primaryKeys
   * @param isSynced
   */
  public async createMany(tableName: TableName, primaryKeys: string[], isSynced: boolean = false): Promise<void> {
    const syncStatusItems: SyncStatusItem[] = [];
    for(const primaryKey of primaryKeys) {
      syncStatusItems.push(this.setBasicProperties(tableName, primaryKey, isSynced));
    }
    await this._table.bulkAdd(syncStatusItems);
    this._change$.next();
  }

  /**
   *
   * @param tableName
   * @param primaryKeys
   * @param isSynced
   */
  public async updateMany(tableName: TableName, primaryKeys: string[], isSynced: boolean = false): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      let syncStatusItems =
        await this._table
          .where('primaryKey')
          .anyOf(primaryKeys)
          .filter(syncStatusItem => syncStatusItem.tableName === tableName)
          .toArray();
      syncStatusItems = syncStatusItems.map(syncStatusItem => {
        return this.setUpdateProperties(syncStatusItem, isSynced);
      });
      await this._table.bulkPut(syncStatusItems);
    });
    this._change$.next();
  }

  /**
   *
   * @param tableName
   * @param primaryKeys
   * @param isSynced
   */
  public async addOrUpdateMany(tableName: TableName, primaryKeys: string[], isSynced: boolean = false): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      let syncStatusItems =
        await this._table
          .where('primaryKey')
          .anyOf(primaryKeys)
          .filter(syncStatusItem => syncStatusItem.tableName === tableName)
          .toArray();
      await this.updateMany(tableName, primaryKeys, isSynced);
      primaryKeys = primaryKeys.filter(_primaryKey => {
        return !syncStatusItems.some(_syncStatusItem => _primaryKey === _syncStatusItem.primaryKey);
      });
      await this.createMany(tableName, primaryKeys, isSynced);
    });
  }

  /**
   *
   * @param primaryKeys
   * @param force
   */
  public async removeMany(primaryKeys: string[], force: boolean = false): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      const syncStatusItems =
        await this._table
          .where('primaryKey')
          .anyOf(primaryKeys)
          .toArray();

      const syncStatusItemsToRemove =
        syncStatusItems.filter(syncStatusItem => this.canBeRemovedWithoutSync(syncStatusItem, force));
      const syncStatusItemsToDelete =
        syncStatusItems.filter(syncStatusItem => !this.canBeRemovedWithoutSync(syncStatusItem, force));

      await this._table.bulkDelete(syncStatusItemsToRemove.map(syncStatusItem => syncStatusItem.id));
      await this.setToDeletedMany(syncStatusItemsToDelete.map(syncStatusItem => syncStatusItem.id));
    });
    this._change$.next();
  }

  /**
   *
   * @param ids
   * @private
   */
  private async setToDeletedMany(ids: number[]): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      let syncStatusItems = await this._table.where('id').anyOf(ids).toArray();
      for(let syncStatusItem of syncStatusItems) {
        syncStatusItem = this.setUpdateProperties(syncStatusItem);
        syncStatusItem.deleted = 1;
      }
      await this._table.bulkPut(syncStatusItems);
    });
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------------------------------------------------

  /**
   *
   * @param tableName
   */
  public async getNotSynced(tableName: TableName = null): Promise<SyncStatusItem[]> {
    if(tableName) {
      return this._table.where({tableName: tableName, isSynced: 0}).toArray();
    }
    return this._table.where('isSynced').equals(0).toArray();
  }

  /**
   *
   * @param tableName
   */
  public async getSynced(tableName: TableName = null): Promise<SyncStatusItem[]> {
    if(tableName) {
      return this._table.where({tableName: tableName, isSynced: 1}).toArray();
    }
    return this._table.where('isSynced').equals(1).toArray();
  }

  /**
   *
   * @param tables
   */
  public async isSynced(tables: string[] = []): Promise<boolean> {
    if(tables.length > 0) {
      for(const table of tables) {
        const syncStatusItems =
          await this._table.where({tableName: table, isSynced: 0}).toArray();
        if(syncStatusItems.length > 0) {
          return false;
        }
      }
      return true;
    }
    else {
      const syncStatusItems =
        await this._table.where('isSynced').equals(0).toArray();
      return syncStatusItems.length <= 0;
    }
  }

  /**
   *
   * @param tableName
   * @param primaryKeys
   */
  public async isSyncedByPrimaryKeys(tableName: TableName, primaryKeys: string[]): Promise<boolean> {
    const syncStatusItems = await this._table.where({
      tableName: tableName,
      isSynced: 0
    }).filter(_syncStatusItem => {
      return primaryKeys.some(_primaryKey => _primaryKey === _syncStatusItem.primaryKey);
    }).toArray();
    return syncStatusItems.length <= 0;
  }

  /**
   * Repairs sync processes that were interrupted before they could be completed.
   *
   * Only entries with `isSyncing === 1` are considered, because that state proves the app had already claimed the
   * record for an upload. A missing local item without that proof is a real data inconsistency and must stay visible.
   *
   * @param tableNames - Only tables synchronised with `syncMode: 'default'`
   */
  public async repairInterruptedSyncStatusItems(tableNames: TableName[]): Promise<void> {
    const syncStatusItems = await this.getToSync();
    let hasRepairedSyncStatusItems = false;

    for(const syncStatusItem of syncStatusItems) {
      if(syncStatusItem.isSyncing !== 1 || !tableNames.includes(syncStatusItem.tableName)) {
        continue;
      }

      const table = db.getSyncTableByTableName(syncStatusItem.tableName);
      if(!table) {
        continue;
      }

      const item = await table.get(syncStatusItem.primaryKey);
      if(item) {
        // The upload never finished, so it is retried
        await this._table.update(syncStatusItem.id, {isSyncing: 0});
      }
      else {
        // The local item disappeared during a started upload, so a tombstone is required
        await this._table.update(syncStatusItem.id, {isSyncing: 0, deleted: 1});
      }
      hasRepairedSyncStatusItems = true;
    }

    if(hasRepairedSyncStatusItems) {
      this._change$.next();
    }
  }

  /**
   * Repairs tombstones whose local item still exists.
   *
   * Every legitimate deletion removes the local item in the same transaction as the tombstone is created, so a
   * tombstone with an existing item is always inconsistent state and must not lead to a deletion on the server.
   * `isSynced` stays untouched, so a pending change is uploaded instead of deleted.
   *
   * @param tableNames - Only tables synchronised with `syncMode: 'default'`
   */
  public async repairResurrectedTombstones(tableNames: TableName[]): Promise<void> {
    const syncStatusItems = await this._table.where('deleted').equals(1).toArray();
    let hasRepairedSyncStatusItems = false;

    for(const syncStatusItem of syncStatusItems) {
      if(!tableNames.includes(syncStatusItem.tableName)) {
        continue;
      }

      const table = db.getSyncTableByTableName(syncStatusItem.tableName);
      if(!table) {
        continue;
      }

      const item = await table.get(syncStatusItem.primaryKey);
      if(item) {
        await this._table.update(syncStatusItem.id, {deleted: 0});
        hasRepairedSyncStatusItems = true;
      }
    }

    if(hasRepairedSyncStatusItems) {
      this._change$.next();
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Functions
  // -------------------------------------------------------------------------------------------------------------------
  /**
   *
   * @param tableName
   * @param primaryKey
   * @param isSynced
   * @private
   */
  private setBasicProperties(tableName: TableName, primaryKey: string, isSynced: boolean = false): SyncStatusItem {
    return {
      id: undefined,
      lastSync: isSynced ? DateTime.local().toISO() : null,
      errorCode: null,
      errorMessage: null,
      isSyncing: 0,
      tableName: tableName,
      isSynced: isSynced ? 1 : 0,
      primaryKey: primaryKey,
      deleted: 0,
      hasConflict: 0
    };
  }
  // -------------------------------------------------------------------------------------------------------------------
  // Synchronisation
  // -------------------------------------------------------------------------------------------------------------------
  /**
   * Claims a single queue entry for an upload atomically.
   *
   * The entry is read and updated within the same transaction, so a stale copy can never restore an entry that was
   * removed or marked as deleted in the meantime.
   *
   * @param syncStatusItemId
   * @returns false if the entry no longer exists, is already synced or became a tombstone
   */
  public async claimForSync(syncStatusItemId: number): Promise<boolean> {
    const isClaimed = await db.transaction('rw', db.syncStatusItems, async () => {
      const syncStatusItem = await this._table.get(syncStatusItemId);
      if(!syncStatusItem || syncStatusItem.isSynced === 1 || syncStatusItem.deleted === 1) {
        return false;
      }
      await this._table.update(syncStatusItemId, {isSyncing: 1});
      return true;
    });

    if(isClaimed) {
      this._change$.next();
    }
    return isClaimed;
  }

  /**
   * Resolves a queue entry whose local item disappeared without a tombstone.
   *
   * An entry that never reached the server is meaningless without its item and is removed completely. An entry with
   * server contact becomes a tombstone, so the server record is deleted instead of blocking the queue forever.
   *
   * @param syncStatusItemId
   * @param hadServerContact
   */
  public async resolveOrphanedEntry(syncStatusItemId: number, hadServerContact: boolean): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      const syncStatusItem = await this._table.get(syncStatusItemId);
      if(!syncStatusItem) {
        return;
      }
      if(hadServerContact) {
        await this._table.update(syncStatusItemId, {
          deleted: 1,
          isSynced: 0,
          isSyncing: 0,
          errorCode: null,
          errorMessage: null
        });
      }
      else {
        await this._table.delete(syncStatusItemId);
      }
    });
    this._change$.next();
  }

  /**
   * Releases a claim that was taken but never sent to the server.
   *
   * @param syncStatusItemId
   */
  public async releaseSyncClaim(syncStatusItemId: number): Promise<void> {
    const isReleased = await db.transaction('rw', db.syncStatusItems, async () => {
      const syncStatusItem = await this._table.get(syncStatusItemId);
      if(!syncStatusItem || syncStatusItem.isSyncing === 0) {
        return false;
      }
      await this._table.update(syncStatusItemId, {isSyncing: 0});
      return true;
    });

    if(isReleased) {
      this._change$.next();
    }
  }

  public async setSyncInProcess(syncStatusItemIds: number[]): Promise<void> {
    let syncStatusItems = await this._table.where('id').anyOf(syncStatusItemIds).toArray();
    syncStatusItems = syncStatusItems.map(syncStatusItem => {
      syncStatusItem.isSyncing = 1;
      return syncStatusItem;
    });
    await this._table.bulkPut(syncStatusItems);
    this._change$.next();
  }

  /**
   *
   * @param itemsToSync
   * @param tableName
   */
  public async setSyncSuccessfully(itemsToSync: SyncItem[], tableName: TableName): Promise<void> {
    const table = db.getSyncTableByTableName(tableName);
    return db.transaction('rw', table, db.syncStatusItems, async () => {
      const itemsFromTable = await table
        .where('uuid')
        .anyOf(itemsToSync.map(itemToSync => itemToSync.uuid))
        .toArray();

      const syncStatusItems = await this._table
        .where('primaryKey')
        .anyOf(itemsToSync.map(item => item.uuid))
        .toArray();

      for(const syncStatusItem of syncStatusItems) {
        syncStatusItem.lastSync = DateTime.local().toISO();
        syncStatusItem.errorCode = null;
        syncStatusItem.errorMessage = null;
        syncStatusItem.hasConflict = 0;
        syncStatusItem.isSyncing = 0;
        // The item was deleted while the upload was running, so the tombstone is kept and a deletion follows
        if(syncStatusItem.deleted === 1) {
          continue;
        }
        const itemFromTable = itemsFromTable.find(_itemFromTable => _itemFromTable.uuid === syncStatusItem.primaryKey);
        const itemToSync = itemsToSync.find(_itemToSync => _itemToSync.uuid === syncStatusItem.primaryKey);
        if(!itemFromTable || !itemToSync) {
          continue;
        }
        const modifiedItemSynced = DateTime.fromISO(itemFromTable.modified);
        const modifiedItemFromTable = DateTime.fromISO(itemToSync.modified);
        if(modifiedItemSynced.equals(modifiedItemFromTable)) {
          syncStatusItem.isSynced = 1;
        }
      }
      await this._table.bulkPut(syncStatusItems);
      this._change$.next();
    });
  }

  /**
   *
   * @param syncStatusItemIds
   * @param syncException
   */
  public async setSyncError(syncStatusItemIds: number[], syncException: SyncException): Promise<void> {
    await db.transaction('rw', db.syncStatusItems, async () => {
      const syncStatusItems = await this._table.where('id').anyOf(syncStatusItemIds).toArray();
      for(const syncStatusItem of syncStatusItems) {
        syncStatusItem.errorCode = syncException.code;
        syncStatusItem.errorMessage = syncException.message;
        syncStatusItem.isSyncing = 0;
        if(syncException.code === 409) {
          syncStatusItem.hasConflict = 1;
        }
      }
      await this._table.bulkPut(syncStatusItems);
    });
    this._change$.next();
  }

  /**
   *
   * @param syncStatusItems
   */
  public async setSyncDeletedItems(syncStatusItems: SyncStatusItem[]): Promise<void> {
    await this._table.bulkDelete(syncStatusItems.map(_syncStatusItem => _syncStatusItem.id));
    this._change$.next();
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Functions
  // -------------------------------------------------------------------------------------------------------------------
  /**
   *
   * @param syncStatusItem
   * @param isSynced
   * @private
   */
  private setUpdateProperties(syncStatusItem: SyncStatusItem, isSynced: boolean = false): SyncStatusItem {
    syncStatusItem.isSynced = isSynced ? 1 : 0;
    if(isSynced) {
      syncStatusItem.lastSync = DateTime.local().toISO();
    }
    return syncStatusItem;
  }

}
